import { Selector } from '@ngxs/store';
import { get as _get } from 'lodash';
import {
  AuctionItem,
  BookValue,
  BookValueDetail,
  ComponentConfig,
  Configuration,
  ConfigurationState,
  ImageMetaData,
  InventoryItem,
  LaunchDarklyService,
  AuctionItemState,
  BookValueSpreadProviderName,
  DetailsState
} from 'remarketing-angular-library';
import { DetailsHeaderQuery } from '../interfaces/detail-header.query';
import { InventoryItemImagesModel } from '../models/inventory-item-images.model';
import { VehicleDetailsQuery } from '../interfaces/vehicle-details.query';
import { CurrencyPipe } from '@angular/common';
import { DetailsLocationQuery } from '../interfaces/detail-location.query';
import { VehicleOptionsMetaData } from 'remarketing-ui-components-lib';
import { ConditionAndDisclosuresQuery } from '../interfaces/condition-and-disclosures.query';
import { SmartauctionFrontendMicroLibraryModule } from 'smartauction-frontend-micro-library';

export class DetailsQueries {
  @Selector([DetailsState.inventoryItem])
  static header(item: InventoryItem): DetailsHeaderQuery {
    return {
      title: `${item.itemDetail.modelYear || ''} ${item.itemDetail.make || ''} ${item.itemDetail.model || ''} ${item
        .itemDetail.trim || ''}`,
      description: `${_get(item, 'itemDetail.bodyConfiguration', '')} ${_get(
        item,
        'itemDetail.powerTrain.driveTrainAbbrev',
        ''
      )}`,
      vin: item.itemDetail.vin,
      stockNumber: item.itemDetail.stockNumber ? item.itemDetail.stockNumber : 'N/A',
      repairCost: 180,
      hasRedLight: _get(item, 'conditionReports[0].limitedArbitration', false),
      hasYellowLight: _get(item, 'conditionReports[0].disclosures.length', 0) > 0,
      yellowLightLabel: _get(item, 'conditionReports[0].disclosures', []),
      inventoryItemUuid: item.inventoryItemUuid,
      odometer: item.itemDetail.odometer,
    };
  }

  @Selector([DetailsState.inventoryItem])
  static images(item: InventoryItem): ImageMetaData[] {
    return InventoryItemImagesModel.fromInventoryItem(item).all();
  }

  @Selector([DetailsState.inventoryItem, DetailsState.distance])
  static vehicleDetails(item: InventoryItem, distance): VehicleDetailsQuery {
    const msrp = _get(item, 'auctionItem.msrpPrice', false);
    const city = _get(item, 'storageLocation.city', false);
    const state = _get(item, 'storageLocation.state', false);
    const location = city && state ? city + ', ' + state : city;

    return {
      seller: item.seller.name,
      odometer: item.itemDetail.odometer,
      location,
      distance: distance,
      titleState: item.itemDetail.titleState || 'N/A',
      msrp: msrp ? new CurrencyPipe('en-US').transform(msrp, null, 'symbol', '1.0-0') : 'N/A',
      type: _get(item, 'itemDetail.vehicleType.vehicleTypeDescription', 'N/A'),
      typeCode: item.itemDetail.vehicleType.vehicleTypeCode,
      exterior: _get(item, 'itemDetail.exteriorColor', 'N/A'),
      interior: this.interiorText(item),
      hits: _get(item, 'hitCount', 0),
      // buyerTips: _get(item, 'buyerTips', []),
      sellerComments: _get(item, 'sellerComment', 'N/A'),
      readyForRetail: true
    };
  }

  static interiorText(item: InventoryItem) {
    const color = _get(item, 'itemDetail.interiorColor', '');
    const type = _get(item, 'itemDetail.interiorType', '');
    const text = [color, type].filter(s => !!s).join(', ');
    return text === '' ? 'N/A' : text;
  }

  @Selector([AuctionItemState.bookValues])
  static bookValues(bookValues: BookValue[]) {
    function reduceToProviders(providers, { providerValue, valueAmount }: BookValueDetail) {
      return { ...providers, [providerValue.replace(/\s+/g, '')]: { valueAmount } };
    }

    return bookValues.reduce(
      (data, bookValue) => {
        if (bookValue.providerName === BookValueSpreadProviderName.BLACK_BOOK) {
          return {
            ...data,
            blackBook: bookValue.values.reduce(reduceToProviders, {})
          };
        }
        if (bookValue.providerName === BookValueSpreadProviderName.NADA) {
          return {
            ...data,
            nada: bookValue.values.reduce(reduceToProviders, {})
          };
        }
        if (bookValue.providerName === BookValueSpreadProviderName.MANHEIM) {
          return {
            ...data,
            manheim: bookValue.values.reduce(reduceToProviders, {})
          };
        }
        return data;
      },
      { blackBook: {}, nada: {}, manheim: {} }
    );
  }

  @Selector([DetailsState.inventoryItem])
  static equipmentAccessories(item: InventoryItem): VehicleOptionsMetaData {
    return {
      highValueOptions: _get(item, 'itemDetail.highValueOptions', []),
      interiorOptions: _get(item, 'itemDetail.interiorOptions', []),
      exteriorOptions: _get(item, 'itemDetail.exteriorOptions', []),
      mechanicalOptions: _get(item, 'itemDetail.mechanicalOptions', [])
    };
  }

  @Selector([DetailsState.inventoryItem])
  static equipmentLongDescriptions(item: InventoryItem): string[] {
    return _get(item, 'itemDetail.vehicleOptionLongDescriptions', []);
  }

  @Selector([DetailsState.inventoryItem])
  static location(item: InventoryItem): DetailsLocationQuery {
    return {
      sellerName: item.seller.name === '' ? null : item.seller.name,
      name: item.storageLocation.name === '' ? null : item.storageLocation.name,
      addressLine1: item.storageLocation.addressLine1 === '' ? null : item.storageLocation.addressLine1,
      addressLine2: item.storageLocation.addressLine2 === '' ? null : item.storageLocation.addressLine2,
      city: item.storageLocation.city === '' ? null : item.storageLocation.city,
      state: item.storageLocation.state === '' ? null : item.storageLocation.state,
      zip: item.storageLocation.zip === '' ? null : item.storageLocation.zip
    };
  }

  @Selector([AuctionItemState.auctionItem])
  static auctionItem(auctionItem: AuctionItem) {
    return auctionItem;
  }

  @Selector([AuctionItemState.auctionItem])
  static postDate(item) {
    return item.postedDateTime;
  }

  @Selector([AuctionItemState.auctionItem])
  static stockNumber(item: AuctionItem) {
    return item.stockNumber ? item.stockNumber.toUpperCase() : 'N/A';
  }

  @Selector([AuctionItemState.auctionItem])
  static defaultSpreadToBook(item: AuctionItem) {
    return item.defaultSpreadToBook;
  }

  @Selector([
    DetailsState.inventoryItem,
    ConfigurationState.coreComponentConfig('vehicle-detail'),
    ConfigurationState.configuration
  ])
  static conditionsAndDisclosures(
    item: InventoryItem,
    vdpConfig: ComponentConfig,
    config: Configuration
  ): ConditionAndDisclosuresQuery {
    const report = _get(item, 'conditionReports[0]', {});
    const itemDetail = _get(item, 'itemDetail');
    const launchDarklyService =  SmartauctionFrontendMicroLibraryModule.InjectorInstance.get<LaunchDarklyService>(LaunchDarklyService);
    return {
      showDamageShading: launchDarklyService.getFeatureFlag('vdp-damageShadingEnabled', false),
      showInspection: _get(vdpConfig, 'subcomponents.inspection-info.visible') && launchDarklyService.getFeatureFlag('vdp-inspectionEnabled', true),
      hasRedLight: _get(report, 'limitedArbitration', false),
      hasYellowLight: _get(report, 'disclosures.length', 0) > 0,
      yellowLightLabel: _get(report, 'disclosures', []),
      tireCondition: {
        tires: _get(report, 'inspection.tires', []),
        disclaimer: _get(vdpConfig, 'subcomponents.tire-info.labels.tire-disclaimer')
      },
      config: {
        forceTireDisclosure: launchDarklyService.getFeatureFlag('vdp-forceTireDisclosureEnabled', false),
        tireInformationVisible: launchDarklyService.getFeatureFlag('vdp-tireInformationEnabled', true),
        showHoursEstimate: launchDarklyService.getFeatureFlag('vdp-hoursEstimateEnabled', false),
        showCost: launchDarklyService.getFeatureFlag('vdp-costEnabled', true),
        showDamageAsList: launchDarklyService.getFeatureFlag('vdp-damageAsListEnabled', false),
        showRepairEstimate: launchDarklyService.getFeatureFlag('vdp-repairEstimateEnabled', true)
      },
      damageData: _get(report, 'inspection.damages', []),
      inspection: _get(report, 'inspection', {}),
      operableFlag: _get(itemDetail, 'operableFlag'),
      disclosureComment: _get(report, 'disclosureComment'),
      legalStatements: Object.values(_get(vdpConfig, 'subcomponents.damage-info.labels')),
      damageType: _get(report, 'sellerDisclosureDamages.damageType', null),
      damageAmount: _get(report, 'sellerDisclosureDamages.damageAmount', null)
    };
  }
}
